"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import {
  DEFAULT_SITE_LANGUAGE,
  readLocalePreference,
  writeLocalePreference,
  type SiteLanguage,
} from "@/lib/i18n/locale";
import { EMPTY_EVENT_LOCATION, resolveRegion, type EventLocationSelection, type SiteRegion } from "@/lib/site-preferences";

type ThemeMode = "dark" | "light";
type SourceFilterMode = "default" | "advanced";
type SourceLanguageGroup = "tr" | "en";

export type SourcePreferenceOption = {
  id: string;
  name: string;
  language: string | null;
  defaultSelected: boolean;
};

type PreferencesContextValue = {
  language: SiteLanguage;
  setLanguage: (language: SiteLanguage) => void;
  sourceOptions: SourcePreferenceOption[];
  selectedSourceGroups: SourceLanguageGroup[];
  setSelectedSourceGroups: (groups: SourceLanguageGroup[]) => void;
  sourceFilterMode: SourceFilterMode;
  setSourceFilterMode: (mode: SourceFilterMode) => void;
  applyDefaultSources: () => void;
  selectedSourceIds: string[];
  setSelectedSourceIds: (ids: string[]) => void;
  selectedSourceNames: string[];
  eventLocation: EventLocationSelection | null;
  setEventLocation: (location: EventLocationSelection | null) => void;
  clearEventLocation: () => void;
  eventRadiusKm: number;
  setEventRadiusKm: (radiusKm: number) => void;
  eventRegion: SiteRegion;
  setEventRegion: (region: SiteRegion) => void;
  resolvedEventRegion: Exclude<SiteRegion, "auto">;
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  /**
   * False on SSR and on the very first client render — flips true
   * once the post-mount hydration effect has copied persisted
   * preferences out of localStorage. Consumers that render
   * conditionally on a preference (e.g. the location-filter banner)
   * MUST gate the conditional on this flag, otherwise the first
   * client render produces different HTML than the server and React
   * raises a hydration mismatch (#418).
   */
  hydrated: boolean;
};

// Note there is no `duudey:lang` key here any more. The language
// preference is the one preference the *server* has to be able to read
// (to decide what `/` does before rendering), and localStorage is
// invisible to the server. It now lives in the `NEXT_LOCALE` cookie,
// read and written through `src/lib/i18n/locale.ts`, which is the single
// source of truth for it. Everything below is genuinely client-only and
// stays in localStorage.
const STORAGE_EVENT_REGION = "duudey:event-region";
const STORAGE_SELECTED_SOURCES = "duudey:selected-sources";
const STORAGE_EVENT_LOCATION = "duudey:event-location";
const STORAGE_EVENT_RADIUS_KM = "duudey:event-radius-km";
const STORAGE_SOURCE_MODE = "duudey:source-mode";
const STORAGE_SOURCE_GROUPS = "duudey:source-groups";
const STORAGE_THEME = "duudey:theme";
const subscribeNoop = () => () => {};

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

function applyTheme(theme: ThemeMode) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

function initialLanguage(): SiteLanguage {
  return readLocalePreference() ?? DEFAULT_SITE_LANGUAGE;
}

function initialTheme(): ThemeMode {
  if (typeof window === "undefined") return "dark";
  const stored = window.localStorage.getItem(STORAGE_THEME);
  if (stored === "dark" || stored === "light") {
    return stored;
  }
  return "dark";
}

function initialEventRegion(): SiteRegion {
  if (typeof window === "undefined") return "auto";
  const stored = window.localStorage.getItem(STORAGE_EVENT_REGION);
  if (stored === "auto" || stored === "us" || stored === "tr" || stored === "eu" || stored === "global") {
    return stored;
  }
  return "auto";
}

function initialSelectedSources(): string[] {
  if (typeof window === "undefined") return [];
  const raw = window.localStorage.getItem(STORAGE_SELECTED_SOURCES);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === "string");
  } catch {
    return [];
  }
}

function initialEventLocation(): EventLocationSelection | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_EVENT_LOCATION);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<EventLocationSelection>;
    if (!parsed || typeof parsed !== "object") return null;

    return {
      countryCode: typeof parsed.countryCode === "string" && parsed.countryCode.trim() ? parsed.countryCode.trim().toLowerCase() : null,
      countryName: typeof parsed.countryName === "string" && parsed.countryName.trim() ? parsed.countryName.trim() : null,
      cityName: typeof parsed.cityName === "string" && parsed.cityName.trim() ? parsed.cityName.trim() : null,
      cityLabel: typeof parsed.cityLabel === "string" && parsed.cityLabel.trim() ? parsed.cityLabel.trim() : null,
      latitude: typeof parsed.latitude === "number" ? parsed.latitude : null,
      longitude: typeof parsed.longitude === "number" ? parsed.longitude : null,
    };
  } catch {
    return null;
  }
}

/**
 * Pick the user's saved event location when present. If nothing has
 * been saved yet, fall back to the server-provided suggestion derived
 * from the negotiated site locale.
 *
 * Why this split exists:
 * - The saved preference is the user's explicit choice and must win.
 * - The suggested value is only a first-visit convenience so the
 *   events view does not start fully unfiltered for visitors whose
 *   locale already strongly suggests a country.
 * - We intentionally do this after mount, not in a state initializer,
 *   so the server render and first client render stay identical and we
 *   avoid a hydration mismatch.
 */
function initialEventLocationWithFallback(
  fallbackLocation: EventLocationSelection | null,
): EventLocationSelection | null {
  return initialEventLocation() ?? fallbackLocation;
}

function initialEventRadiusKm(): number {
  if (typeof window === "undefined") return 200;
  const raw = window.localStorage.getItem(STORAGE_EVENT_RADIUS_KM);
  const parsed = Number.parseInt(raw ?? "", 10);
  if (!Number.isFinite(parsed)) return 200;
  return Math.min(2000, Math.max(10, parsed));
}

function initialSourceMode(): SourceFilterMode {
  if (typeof window === "undefined") return "default";
  const stored = window.localStorage.getItem(STORAGE_SOURCE_MODE);
  if (stored === "default" || stored === "advanced") {
    return stored;
  }
  return "default";
}

function initialSourceGroups(): SourceLanguageGroup[] {
  if (typeof window === "undefined") return ["tr", "en"];
  const raw = window.localStorage.getItem(STORAGE_SOURCE_GROUPS);
  if (!raw) return ["tr", "en"];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return ["tr", "en"];
    const filtered = parsed.filter((item): item is SourceLanguageGroup => item === "tr" || item === "en");
    return filtered.length > 0 ? filtered : ["tr", "en"];
  } catch {
    return ["tr", "en"];
  }
}

export function PreferencesProvider({
  children,
  initialSuggestedEventLocation = null,
}: {
  children: React.ReactNode;
  initialSuggestedEventLocation?: EventLocationSelection | null;
}) {
  // CRITICAL: every initial state below is the SSR-deterministic
  // default — NEVER read localStorage in a useState initializer.
  //
  // Doing so would return the SSR default on the server (where
  // ``window`` is undefined) but the persisted value on the first
  // client render, which is a guaranteed React #418 hydration
  // mismatch any time the user has previously set anything (a
  // location filter, a theme, a language, etc.). Persisted values
  // are read in the post-mount ``useEffect`` further down, gated
  // on ``hydrated`` so the localStorage writer effects don't fire
  // with the SSR defaults and clobber the persisted values during
  // the first effect pass.
  const [language, setLanguageState] = useState<SiteLanguage>(DEFAULT_SITE_LANGUAGE);
  const [sourceOptions, setSourceOptions] = useState<SourcePreferenceOption[]>([]);
  const [selectedSourceGroups, setSelectedSourceGroupsState] = useState<SourceLanguageGroup[]>(["tr", "en"]);
  const [sourceFilterMode, setSourceFilterModeState] = useState<SourceFilterMode>("default");
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [eventLocation, setEventLocationState] = useState<EventLocationSelection | null>(null);
  const [eventRadiusKm, setEventRadiusKmState] = useState<number>(200);
  const [eventRegion, setEventRegion] = useState<SiteRegion>("auto");
  const [theme, setTheme] = useState<ThemeMode>("dark");
  // Hydration-safe gate: SSR and the first client render both see
  // `false`, then the second client render flips to `true`. Writer
  // effects use this to avoid clobbering persisted values with SSR
  // defaults during the mount hand-off.
  const hydrated = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  );

  /**
   * Change the language *and* persist it, in that order, synchronously.
   *
   * The ordering is load-bearing, not stylistic. `SiteShell`'s language
   * switcher calls this and then immediately calls `router.push("/")`
   * when the target is the English root. That push fires an RSC request
   * for `/`, and `/` is the one URL whose response depends on the
   * cookie. If the cookie were written later — from an effect, say —
   * the request would still carry the *old* language and the proxy would
   * answer with a 307 straight back to where the reader started, so the
   * switcher would appear to do nothing.
   *
   * `document.cookie` is a synchronous assignment, so writing it here,
   * inside the click handler, closes that window entirely.
   */
  const setLanguage = useCallback((next: SiteLanguage) => {
    writeLocalePreference(next);
    setLanguageState(next);
  }, []);

  const applyDefaultSources = useMemo(
    () => () => {
      setSelectedSourceIds(sourceOptions.filter((option) => option.defaultSelected).map((option) => option.id));
    },
    [sourceOptions],
  );

  const setSourceFilterMode = useMemo(
    () => (mode: SourceFilterMode) => {
      setSourceFilterModeState(mode);
      if (mode === "default") {
        setSelectedSourceIds(sourceOptions.filter((option) => option.defaultSelected).map((option) => option.id));
      }
    },
    [sourceOptions],
  );

  const setSelectedSourceGroups = useMemo(
    () => (groups: SourceLanguageGroup[]) => {
      const normalized: SourceLanguageGroup[] = groups.length > 0 ? groups : ["tr", "en"];
      setSelectedSourceGroupsState(normalized);
      if (sourceFilterMode === "default") {
        setSelectedSourceIds(
          sourceOptions
            .filter((option) => option.language === null || normalized.includes(option.language as SourceLanguageGroup))
            .map((option) => option.id),
        );
      }
    },
    [sourceFilterMode, sourceOptions],
  );

  // Hydrate persisted preferences from localStorage. Runs once after
  // mount, after the SSR-matching initial render has already painted.
  // We read each key with the existing helpers (which return the SSR
  // default when the key is missing or invalid) and call the setters
  // unconditionally; ``hydrated`` flips on the next client render and
  // unlocks the writer effects.
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect --
       This one-time mount pass copies persisted localStorage values
       into React state after the SSR-matching first render. */
    // Read-only on purpose: this uses the *raw* state setter, never
    // `setLanguage`. Effects run child-first, so `SiteShell`'s
    // URL-sync effect has already written the cookie for the page the
    // reader actually opened; writing here as well could stamp a stale
    // value over it depending on effect ordering. The cookie has
    // exactly one writer path (`setLanguage`), and this is not it.
    setLanguageState(initialLanguage());
    setTheme(initialTheme());
    applyTheme(initialTheme());
    setEventRegion(initialEventRegion());
    setSelectedSourceIds(initialSelectedSources());
    setSelectedSourceGroupsState(initialSourceGroups());
    setSourceFilterModeState(initialSourceMode());
    setEventLocationState(initialEventLocationWithFallback(initialSuggestedEventLocation));
    setEventRadiusKmState(initialEventRadiusKm());
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [initialSuggestedEventLocation]);

  useEffect(() => {
    let cancelled = false;

    async function loadSourceOptions() {
      try {
        const response = await fetch("/api/preferences/bootstrap", { cache: "no-store" });
        if (!response.ok) return;

        const payload = (await response.json()) as { sources?: SourcePreferenceOption[] };
        const options = payload.sources ?? [];
        if (cancelled) return;
        setSourceOptions(options);

        setSelectedSourceIds((current) => {
          if (current.length > 0 && sourceFilterMode === "advanced") {
            return current.filter((id) => options.some((option) => option.id === id));
          }
          return options
            .filter((option) => option.language === null || selectedSourceGroups.includes(option.language as SourceLanguageGroup))
            .map((option) => option.id);
        });
      } catch {
        // Keep defaults when bootstrap endpoint is unavailable.
      }
    }

    void loadSourceOptions();
    return () => {
      cancelled = true;
    };
  }, [sourceFilterMode, selectedSourceGroups]);

  // All writer effects below are gated on ``hydrated`` so they don't
  // fire with the SSR defaults and clobber persisted values during
  // the first effect pass (writers run AFTER the hydration effect on
  // the same render, but the hydration effect's ``setHydrated(true)``
  // only takes effect on the next render).
  // No writer effect for the language.
  //
  // Every other preference here is written from an effect keyed on its
  // own state, which is fine because nothing on the server reads them.
  // The language is different: the server reads it on `/`, so *when*
  // it is written matters, and an effect fires too late to beat the
  // navigation the switcher starts. `setLanguage` above writes it
  // inline instead. See the comment there.

  useEffect(() => {
    if (!hydrated) return;
    window.localStorage.setItem(STORAGE_THEME, theme);
    applyTheme(theme);
  }, [hydrated, theme]);

  useEffect(() => {
    if (!hydrated) return;
    window.localStorage.setItem(STORAGE_EVENT_REGION, eventRegion);
  }, [hydrated, eventRegion]);

  useEffect(() => {
    if (!hydrated) return;
    window.localStorage.setItem(STORAGE_SELECTED_SOURCES, JSON.stringify(selectedSourceIds));
  }, [hydrated, selectedSourceIds]);

  useEffect(() => {
    if (!hydrated) return;
    window.localStorage.setItem(STORAGE_SOURCE_GROUPS, JSON.stringify(selectedSourceGroups));
  }, [hydrated, selectedSourceGroups]);

  useEffect(() => {
    if (!hydrated) return;
    window.localStorage.setItem(STORAGE_SOURCE_MODE, sourceFilterMode);
  }, [hydrated, sourceFilterMode]);

  const selectedSourceNames = useMemo(() => {
    const selected = sourceOptions.filter((option) => selectedSourceIds.includes(option.id)).map((option) => option.name);
    if (selected.length > 0) return selected;
    return sourceOptions
      .filter((option) => option.language === null || selectedSourceGroups.includes(option.language as SourceLanguageGroup))
      .map((option) => option.name);
  }, [sourceOptions, selectedSourceIds, selectedSourceGroups]);

  const setEventLocation = useMemo(
    () => (location: EventLocationSelection | null) => {
      const normalized = location
        ? {
            countryCode: location.countryCode ? location.countryCode.trim().toLowerCase() : null,
            countryName: location.countryName?.trim() || null,
            cityName: location.cityName?.trim() || null,
            cityLabel: location.cityLabel?.trim() || null,
            latitude: typeof location.latitude === "number" ? location.latitude : null,
            longitude: typeof location.longitude === "number" ? location.longitude : null,
          }
        : null;
      setEventLocationState(normalized);
    },
    [],
  );

  const clearEventLocation = useMemo(
    () => () => {
      setEventLocationState(null);
    },
    [],
  );

  const setEventRadiusKm = useMemo(
    () => (radiusKm: number) => {
      if (!Number.isFinite(radiusKm)) return;
      setEventRadiusKmState(Math.min(2000, Math.max(10, Math.round(radiusKm))));
    },
    [],
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      sourceOptions,
      selectedSourceGroups,
      setSelectedSourceGroups,
      sourceFilterMode,
      setSourceFilterMode,
      applyDefaultSources,
      selectedSourceIds,
      setSelectedSourceIds,
      selectedSourceNames,
      eventLocation,
      setEventLocation,
      clearEventLocation,
      eventRadiusKm,
      setEventRadiusKm,
      eventRegion,
      setEventRegion,
      resolvedEventRegion: resolveRegion(eventRegion, language),
      theme,
      setTheme,
      toggleTheme: () => setTheme((current) => (current === "dark" ? "light" : "dark")),
      hydrated,
    }),
    [
      language,
      setLanguage,
      sourceOptions,
      selectedSourceGroups,
      setSelectedSourceGroups,
      sourceFilterMode,
      setSourceFilterMode,
      applyDefaultSources,
      selectedSourceIds,
      selectedSourceNames,
      eventLocation,
      setEventLocation,
      clearEventLocation,
      eventRadiusKm,
      setEventRadiusKm,
      eventRegion,
      theme,
      hydrated,
    ],
  );

  useEffect(() => {
    if (!hydrated) return;
    window.localStorage.setItem(STORAGE_EVENT_LOCATION, JSON.stringify(eventLocation ?? EMPTY_EVENT_LOCATION));
  }, [hydrated, eventLocation]);

  useEffect(() => {
    if (!hydrated) return;
    window.localStorage.setItem(STORAGE_EVENT_RADIUS_KM, String(eventRadiusKm));
  }, [hydrated, eventRadiusKm]);

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error("usePreferences must be used inside PreferencesProvider");
  }
  return context;
}
